import { useQuery } from "@apollo/client";
import { GET_POSTS } from "./queries";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { formatDate } from "./utils/format-date";
import Skeleton from "react-loading-skeleton";

export default function PostPage() {
  const perPage = 18;
  const navigate = useNavigate();
  const location = useLocation();

  // Keep track of cursors for pages
  const [page, setPage] = useState(1);
  const [cursors, setCursors] = useState<(string | null)[]>([null]);

  const {
    loading: loadingPosts,
    data: postsData,
    error: postsError,
    fetchMore,
  } = useQuery(GET_POSTS, {
    variables: { first: perPage, after: cursors[page - 1] }, // 🎯 use cursor for this page
    fetchPolicy: "cache-and-network",
  });

  // If user types /articles?page=2 manually
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get("page") || "1", 10);
    if (pageParam !== page) {
      setPage(pageParam);
    }
  }, [location.search, page]);

  if (postsError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 font-bold">Error: {postsError?.message}</p>
      </div>
    );
  }

  const posts = postsData?.posts?.nodes;
  const pageInfo = postsData?.posts?.pageInfo;

  const handleNextPage = async () => {
    if (!pageInfo?.hasNextPage) return;

    const { data: fetchMoreData } = await fetchMore({
      variables: {
        first: perPage,
        after: pageInfo.endCursor,
      },
    });

    if (fetchMoreData?.posts.pageInfo.endCursor) {
      setCursors((prev) => [...prev, fetchMoreData.posts.pageInfo.endCursor]);
      setPage((prev) => prev + 1);
      navigate(`?page=${page + 1}`);
      window.scrollTo(0, 0);
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage((prev) => prev - 1);
      navigate(`?page=${page - 1}`);
      window.scrollTo(0, 0);
    }
  };

  return (
    <>
      <title>Articles</title>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold text-center mb-12">Articles</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loadingPosts
            ? [...Array(12)].map((_, i) => (
                <div key={i} className="p-4 border-2 border-gray-300 rounded-lg bg-white">
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="sm:w-5/12">
                      <Skeleton
                        width="100%"
                        className="aspect-square"
                        highlightColor="#0a5640"
                      />
                    </div>
                    <div className="sm:w-7/12">
                      <h5>
                        <Skeleton
                          count={3}
                          height="1.2rem"
                          className="my-1"
                          highlightColor="#0a5640"
                        />
                      </h5>
                      <Skeleton
                        height="1rem"
                        width="60%"
                        highlightColor="#0a5640"
                      />
                    </div>
                  </div>
                </div>
              ))
            : posts?.map((post: any) => (
                <Link
                  to={`/post/${post.slug}`}
                  className="no-underline text-inherit group"
                  key={post.id}
                >
                  <div className="p-4 border-2 border-gray-300 rounded-lg hover:border-[#0a5640] transition-colors duration-300 h-full bg-white group-hover:underline decoration-[#0a5640]">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="sm:w-5/12 flex-shrink-0">
                        <div className="relative aspect-square overflow-hidden rounded bg-gray-100 image-wrapper">
                          <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] animate-shimmer skeleton-loader"></div>
                          {post.featuredImage?.node?.mediaDetails?.sizes && (
                            <img
                              src={
                                post.featuredImage.node.mediaDetails.sizes.find(
                                  (s: any) => s.name === "thumbnail"
                                )?.sourceUrl
                              }
                              alt="Post"
                              className="absolute inset-0 w-full h-full object-cover opacity-0 transition-opacity duration-300 z-10"
                              onLoad={(e) => {
                                const container = e.currentTarget.parentElement;
                                if (container) {
                                  const loader = container.querySelector('.skeleton-loader') as HTMLElement;
                                  if (loader) loader.style.display = 'none';
                                  e.currentTarget.style.opacity = '1';
                                }
                              }}
                            />
                          )}
                        </div>
                      </div>
                      <div className="sm:w-7/12">
                        <h5
                          className="text-lg font-bold mb-2 line-clamp-3 group-hover:text-[#0a5640]"
                          dangerouslySetInnerHTML={{
                            __html: post.title,
                          }}
                        />
                        {post.author?.node?.name && (
                          <p className="text-sm text-gray-600 mb-1">
                            <strong className="text-gray-900">Author:</strong> {post.author.node.name}
                          </p>
                        )}
                        <p className="text-sm text-gray-600">
                          <strong className="text-gray-900">Date:</strong> {formatDate(post.date)}
                        </p>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
        </div>

        <div className="flex justify-center items-center mt-12 mb-8 gap-4">
          <button
            className="px-6 py-2 border-2 border-[#0a5640] text-[#0a5640] font-bold rounded-md hover:bg-[#0a5640] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={page <= 1}
            onClick={handlePreviousPage}
          >
            Previous
          </button>
          <span className="text-xl font-medium">Page {page}</span>
          <button
            className="px-6 py-2 border-2 border-[#0a5640] text-[#0a5640] font-bold rounded-md hover:bg-[#0a5640] hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!pageInfo?.hasNextPage}
            onClick={handleNextPage}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}
